'use client'

import 'react-image-crop/dist/ReactCrop.css'

import { Alert, Button, Input } from '@chain-zen/ui'
import Compressor from 'compressorjs'
import dynamic from 'next/dynamic'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { Crop } from 'react-image-crop'

import { useUploadPicture } from '../../hooks/use-upload-picture'
import { loadImage } from '../../utils/crop-image'
import { setBlob } from '../../utils/set-blob'

const ReactCrop = dynamic(() => import('react-image-crop').then((mod) => mod.default), { ssr: false })

interface IProfilePictureUploaderProps {
  alertError?: string
  isRemovingPhoto?: boolean
  onImageUpload: (fileObject: Blob) => void
  onRemovePhoto?: () => void
  removeButtonText?: string
  showRemoveButton?: boolean
  uploadButtonText?: string
}

/* 
 This component is used to upload profile picture or workspace logo.
 This is used in the onboarding process and settings pages.
*/
export const ProfilePictureUploader = (props: IProfilePictureUploaderProps) => {
  const {
    alertError,
    onImageUpload,
    showRemoveButton = false,
    onRemovePhoto,
    isRemovingPhoto,
    uploadButtonText = 'Upload Picture',
    removeButtonText = 'Remove Photo'
  } = props

  const [localURL, setLocalURL] = useState('')
  const [isFileLimitExceeded, setIsFileLimitExceeded] = useState<boolean>(false)
  const [croppedResult, setCroppedResult] = useState<Blob | null>(null)
  const imgRef = useRef(null)
  const previewCanvasRef = useRef(null)
  const [crop, setCrop] = useState<Crop>({ height: 0, unit: '%', width: 100, x: 0, y: 0 })
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null)

  const { getSignedURL, isLoading } = useUploadPicture()

  useEffect(() => {
    setBlob({ completedCrop, imgRef, previewCanvasRef, setCroppedResult })
  }, [completedCrop])

  useEffect(() => {
    if (croppedResult) handleUpload()
  }, [croppedResult])

  const onLoad = (img: React.SyntheticEvent<HTMLImageElement>) => {
    loadImage({ img: img.currentTarget, imgRef, setCrop, setNewCompletedCrop: setCompletedCrop })
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const fileSizeInMB = file.size / (1024 * 1024)
    const MAX_FILE_MB_SIZE = 16

    if (fileSizeInMB > MAX_FILE_MB_SIZE) {
      setIsFileLimitExceeded(true)
    } else {
      setIsFileLimitExceeded(false)
      const url = URL.createObjectURL(file)
      setLocalURL(url)
    }
  }

  const handleChooseFile = () => {
    setIsFileLimitExceeded(false)
    document.getElementById('choosePicture')?.click()
  }

  const handleUpload = useCallback(async () => {
    if (!croppedResult) return

    new Compressor(croppedResult, {
      quality: 0.5,
      success: async () => {
        onImageUpload(croppedResult)
        getSignedURL(croppedResult.type)
      }
    })
  }, [croppedResult, getSignedURL, onImageUpload])

  return (
    <div>
      <div className="hidden">
        <ReactCrop crop={crop} circularCrop={true} onChange={setCrop} onComplete={setCompletedCrop}>
          {!!localURL && <img src={localURL} onLoad={onLoad} alt="Crop preview" />}
        </ReactCrop>
        <canvas ref={previewCanvasRef} />
      </div>
      {!!alertError && <Alert variant="warning" className="mb-4" description={alertError} />}
      <div className="text-center">
        {!!isLoading && (
          <div className="absolute mt-23 ml-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}

        <div className="mt-5 flex justify-center gap-4">
          <Input
            type="file"
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              ;(event.target as HTMLInputElement).value = ''
            }}
            id="choosePicture"
            accept="image/png, image/jpeg"
            onChange={handleChange}
            className="hidden"
            aria-label="choosePicture"
          />

          {!!isFileLimitExceeded && <p className="text-destructive text-sm mb-2">File size must be less than 16MB</p>}

          <Button onClick={handleChooseFile} disabled={isLoading}>
            {!!isLoading && (
              <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full mr-2" />
            )}
            {uploadButtonText}
          </Button>

          {!!showRemoveButton && !!onRemovePhoto && (
            <Button
              loading={isRemovingPhoto}
              variant="outline"
              onClick={onRemovePhoto}
              className="text-destructive border-destructive hover:bg-destructive/10"
            >
              {isRemovingPhoto ? 'Removing...' : removeButtonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
